import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableRow,
  Typography,
  Select,
  MenuItem,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { Box } from '@mui/system';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import BrudCrumbs from '../../components/BreadCrumbs';
import Label from '../../components/Label';
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import UserListHead from '../../components/_dashboard/user/UserListHead';
import UserMoreMenu from '../../components/_dashboard/user/UserMoreMenu';
import { NOT_AVAILABLE } from '../Global/Constants';
import Loader from '../Global/Loader';
import { fDate, fDateTime2 } from '../../utils/formatTime';
import CallCenterService from '../services/CallCenterService';
import prospectService from '../services/prospectService';
import ProspectToolbar from './ProspectToolbar';
import useProspectSelector from '../../store/Slice/Prospect/ProspectSelector';
import { getProspectGridData } from '../../store/Slice/Prospect/ProspectSlice';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import useDebounce from '../../utils/useDebounce';

const TABLE_HEAD = [
  { id: 'id', label: 'PROSPECT ID', alignRight: false },
  { id: 'created', label: 'PROSPECT DATE', alignRight: false },
  { id: 'randomPhoneNumber', label: 'MOBILE', alignRight: false },
  { id: 'name', label: 'NAME', alignRight: false },
  { id: 'city', label: 'CITY', alignRight: false },
  { id: 'loanType', label: 'LOAN TYPE', alignRight: false },
  { id: 'source', label: 'SOURCE', alignRight: false },
  { id: 'status', label: 'STATUS', alignRight: false },
  { id: 'modifiedOn', label: 'LAST UPDATE ON', alignRight: false },
  { id: 'createdBy', label: 'ASSIGN TO', alignRight: false },
  { id: 'action', label: 'ACTION', alignRight: false, align: 'center' }
];

const StyledTableRow = styled(TableRow)(() => ({
  backgroundColor: '#fff',
  '&:nth-of-type(even)': { backgroundColor: '#fafbfc' },
  '&:hover': { backgroundColor: '#f1f5f9 !important' },
  '&:last-child td, &:last-child th': { border: 0 }
}));

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 11.5,
    fontWeight: 700,
    padding: '12px 14px',
    backgroundColor: '#ffffff',
    color: '#64748b',
    borderBottom: '1px solid #e2e8f0',
    letterSpacing: '0.5px'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 13,
    padding: '12px 14px',
    color: '#334155',
    borderBottom: '1px solid #f1f5f9'
  }
}));

const { ProspectDelete } = prospectService;
const { UpdateStatus } = CallCenterService;

const getStatusColor = (status) => {
  const s = (status || '').toLowerCase();
  if (s.includes('not assigned') || s.includes('reject')) return 'error';
  if (s.includes('submit')) return 'info';
  if (s.includes('assign') || s.includes('pend') || s.includes('process')) return 'warning';
  if (s.includes('approv') || s.includes('success') || s.includes('complet')) return 'success';
  return 'primary';
};

export default function ProspectListView() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { ProspectGrid } = useProspectSelector();
  const { LoginData } = useLoginSelector();

  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('name');
  const [selected, setSelected] = useState([]);
  const [filters, setFilters] = useState({
    name: '',
    status: ' ',
    source: ' ',
    city: null,
    dates: [{ startDate: null, endDate: null, key: 'selection' }]
  });
  const [ui, setUi] = useState({
    openAssign: false,
    assignValue: ' ',
    alertMsg: false,
    openDelete: false,
    leadId: null,
    page: 0,
    hasNextPage: true,
    refresh: Date.now()
  });

  const debouncedSearch = useDebounce(filters.name, 600);

  const handleRequestSort = useCallback(
    (e, property) => {
      if (property === 'action') return;
      setOrder((prev) => (orderBy === property && prev === 'asc' ? 'desc' : 'asc'));
      setOrderBy(property);
    },
    [orderBy]
  );

  const handleSelectAllClick = useCallback(
    (e) => {
      setSelected(
        e.target.checked ? (ProspectGrid?.prospects || []).filter((item) => !item.isDisabled) : []
      );
    },
    [ProspectGrid]
  );

  const handleAssignSubmit = useCallback(async () => {
    if (ui.assignValue === ' ') {
      setUi((prev) => ({ ...prev, alertMsg: true }));
      return;
    }
    const payload = selected.map((item) => ({
      conversionDetailsModel: { assignedTo: ui.assignValue },
      leadId: item.leadId,
      stageId: item.stageId,
      stageStatusCode: 'ASSIGNED',
      userId: LoginData?.userId
    }));
    const res = await UpdateStatus(payload, LoginData?.jwt);
    if (res?.msg === 'Forbidden') navigate('/');
    if (res?.status === 'success') {
      setUi((prev) => ({ ...prev, assignValue: ' ', openAssign: false, refresh: Date.now() }));
      setSelected([]);
    }
  }, [ui.assignValue, selected, LoginData, navigate]);

  const handleDelete = useCallback(async () => {
    const res = await ProspectDelete(
      { companyId: LoginData?.companyId, userId: LoginData?.userId, leadId: ui.leadId },
      LoginData?.jwt
    );
    if (res?.msg === 'Forbidden') navigate('/');
    if (res?.status === 'success')
      setUi((prev) => ({ ...prev, openDelete: false, refresh: Date.now() }));
  }, [ui.leadId, LoginData, navigate]);

  const credentials = useMemo(
    () => ({
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      accessLevelId: LoginData?.accessLevelId,
      leadsFilterModel: {
        fromLeadDate: filters.dates[0].startDate ? fDateTime2(filters.dates[0].startDate) : null,
        toLeadDate: filters.dates[0].endDate ? fDateTime2(filters.dates[0].endDate) : null,
        leadStageStatusId: filters.status !== ' ' ? filters.status : null,
        sourceTypeId: filters.source !== ' ' ? filters.source : null,
        cityId: filters.city ? filters.city.cityId : null
      },
      entityListRequestModel: {
        searchLiteral: debouncedSearch?.length >= 2 ? debouncedSearch : null,
        pageSize: 10,
        pageNumber: ui.page + 1,
        sortOrder: order
      }
    }),
    [
      LoginData,
      filters.dates,
      filters.status,
      filters.source,
      filters.city,
      debouncedSearch,
      ui.page,
      order
    ]
  );

  useEffect(() => {
    dispatch(getProspectGridData(credentials));
  }, [dispatch, credentials, ui.refresh]);
  useEffect(() => {
    setUi((prev) => ({ ...prev, hasNextPage: (ProspectGrid?.prospects || []).length >= 10 }));
  }, [ProspectGrid]);

  return (
    <Page title="Kpros by Kalolytic Solutions || Prospect">
      <Container maxWidth="xl" sx={{ pb: 6, pt: 2 }}>
        {/* Top Header Row with Icon, Title, and Breadcrumb */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 3.5, gap: 2 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: '12px',
                bgcolor: '#e0f2fe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0284c7'
              }}
            >
              <PeopleAltOutlinedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#0f172a',
                  fontSize: { xs: '20px', sm: '24px' },
                  letterSpacing: '-0.3px',
                  mb: 0.25
                }}
              >
                Prospect
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px' }}>
                Track, verify and manage customer prospects and assignments.
              </Typography>
            </Box>
          </Stack>

          <Box>
            <BrudCrumbs />
          </Box>
        </Stack>

        <Card
          sx={{
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            overflow: 'hidden',
            bgcolor: '#ffffff'
          }}
        >
          <ProspectToolbar
            selected={selected}
            filterName={filters.name}
            setFilterName={(val) => setFilters((p) => ({ ...p, name: val }))}
            onFilterName={(e) => setFilters((p) => ({ ...p, name: e.target.value }))}
            parent="prospect"
            pageMenu={[]}
            selectStatus={filters.status}
            setSelectStatus={(val) => setFilters((p) => ({ ...p, status: val }))}
            onFilterStatus={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
            selectSource={filters.source}
            setSelectSource={(val) => setFilters((p) => ({ ...p, source: val }))}
            onFilterSource={(e) => setFilters((p) => ({ ...p, source: e.target.value }))}
            cityListData={filters.city}
            setCityListData={(val) => setFilters((p) => ({ ...p, city: val }))}
            filterDatesRange={filters.dates}
            setFilterDatesRange={(val) => setFilters((p) => ({ ...p, dates: val }))}
            onAssignSubmit={handleAssignSubmit}
            openAssign={ui.openAssign}
            setOpenAssign={(val) => setUi((p) => ({ ...p, openAssign: val }))}
            assignValue={ui.assignValue}
            setAssignValue={(val) => setUi((p) => ({ ...p, assignValue: val }))}
            alertMsg={ui.alertMsg}
            setAlertMsg={(val) => setUi((p) => ({ ...p, alertMsg: val }))}
          />
          <Scrollbar>
            <TableContainer sx={{ minHeight: 380, overflow: 'auto' }}>
              <Table sx={{ minWidth: 800 }}>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={(ProspectGrid?.prospects || []).length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                  disableBoxTrue={(ProspectGrid?.prospects || []).filter((i) => i.isDisabled)}
                  disableBoxFalse={(ProspectGrid?.prospects || []).filter((i) => !i.isDisabled)}
                  isCheckBox="true"
                />
                <TableBody>
                  {ProspectGrid?.prospects?.map((item, index) => {
                    const isSelected = selected.includes(item);
                    return (
                      <StyledTableRow
                        hover
                        key={index}
                        role="checkbox"
                        selected={isSelected}
                        aria-checked={isSelected}
                      >
                        <TableCell padding="checkbox" sx={{ pl: 2.5 }}>
                          <Checkbox
                            className={item.isDisabled ? 'disabledDocument' : 'enabledDocument'}
                            checked={isSelected}
                            onChange={() =>
                              setSelected((prev) =>
                                isSelected ? prev.filter((i) => i !== item) : [...prev, item]
                              )
                            }
                            sx={{
                              color: '#cbd5e1',
                              '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0284c7' }
                            }}
                          />
                        </TableCell>
                        <StyledTableCell style={{ fontWeight: 600, color: '#0284c7' }}>
                          {item.leadId || NOT_AVAILABLE}
                        </StyledTableCell>
                        <StyledTableCell style={{ color: '#64748b' }}>
                          {item.leadDate ? fDate(item.leadDate) : NOT_AVAILABLE}
                        </StyledTableCell>
                        <StyledTableCell style={{ color: '#64748b' }}>
                          {item.mobile || NOT_AVAILABLE}
                        </StyledTableCell>
                        <StyledTableCell style={{ fontWeight: 600, color: '#0f172a' }}>
                          {item.customerName || NOT_AVAILABLE}
                        </StyledTableCell>
                        <StyledTableCell style={{ color: '#64748b' }}>
                          {item.cityName || NOT_AVAILABLE}
                        </StyledTableCell>
                        <StyledTableCell style={{ color: '#64748b' }}>
                          {item.loanName || NOT_AVAILABLE}
                        </StyledTableCell>
                        <StyledTableCell style={{ color: '#64748b' }}>
                          {item.sourceTypeName || NOT_AVAILABLE}
                        </StyledTableCell>
                        <StyledTableCell>
                          <Label
                            variant="ghost"
                            color={getStatusColor(item.stageStatusName)}
                            style={{
                              textTransform: 'capitalize'
                            }}
                          >
                            {item.stageStatusName || NOT_AVAILABLE}
                          </Label>
                        </StyledTableCell>
                        <StyledTableCell style={{ color: '#64748b' }}>
                          {item.lastUpdatedOn ? fDate(item.lastUpdatedOn) : NOT_AVAILABLE}
                        </StyledTableCell>
                        <StyledTableCell style={{ color: '#64748b' }}>
                          {item.assignedToName || NOT_AVAILABLE}
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          <UserMoreMenu
                            rowId={item.leadId}
                            parent="prospect"
                            companyId={item.companyId}
                            pageMenu={[]}
                            handleDelete={handleDelete}
                            handleOpenDelete={() =>
                              setUi((p) => ({ ...p, openDelete: true, leadId: item.leadId }))
                            }
                          />
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
                {(!ProspectGrid?.prospects || ProspectGrid.prospects.length === 0) && (
                  <TableBody>
                    <TableRow>
                      <StyledTableCell align="center" colSpan={TABLE_HEAD.length + 1}>
                        <SearchNotFound searchQuery={filters.name} />
                      </StyledTableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          {/* Modern Pagination Footer */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems="center"
            justifyContent="space-between"
            sx={{ p: 2, px: 3, borderTop: '1px solid #f1f5f9', bgcolor: '#ffffff', gap: 2 }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography sx={{ fontSize: '13px', color: '#64748b' }}>Showing</Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                {ProspectGrid?.prospects?.length || 0} records
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography sx={{ fontSize: '13px', color: '#64748b' }}>
                Page <span style={{ fontWeight: 600, color: '#0f172a' }}>{ui.page + 1}</span>
              </Typography>

              <Stack direction="row" spacing={1}>
                <IconButton
                  disabled={ui.page === 0}
                  onClick={() => setUi((p) => ({ ...p, page: Math.max(0, p.page - 1) }))}
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    p: 0.75,
                    bgcolor: '#ffffff',
                    color: '#0f172a',
                    '&.Mui-disabled': { opacity: 0.4, bgcolor: '#f8fafc' }
                  }}
                >
                  <ChevronLeftIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton
                  disabled={!ui.hasNextPage}
                  onClick={() => setUi((p) => ({ ...p, page: p.page + 1 }))}
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    p: 0.75,
                    bgcolor: '#ffffff',
                    color: '#0f172a',
                    '&.Mui-disabled': { opacity: 0.4, bgcolor: '#f8fafc' }
                  }}
                >
                  <ChevronRightIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
            </Stack>
          </Stack>
        </Card>

        <Dialog
          open={ui.openDelete}
          onClose={() => setUi((p) => ({ ...p, openDelete: false }))}
          fullWidth
          PaperProps={{
            sx: { borderRadius: '16px', overflow: 'hidden' }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 2,
              borderBottom: '1px solid #e2e8f0'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', color: '#0f172a' }}>
              Delete Prospect
            </Typography>
          </Box>
          <Box m={2}>
            <Typography sx={{ color: '#64748b', mb: 2 }}>
              Are you sure you want to delete?
            </Typography>
            <DialogActions sx={{ px: 0 }}>
              <Button
                onClick={() => setUi((p) => ({ ...p, openDelete: false }))}
                variant="outlined"
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  borderColor: '#e2e8f0',
                  color: '#637381',
                  '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' }
                }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                onClick={handleDelete}
                sx={{
                  bgcolor: '#0284c7',
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 4px 14px rgba(2,132,199,0.25)',
                  '&:hover': { bgcolor: '#0369a1' }
                }}
              >
                Delete
              </Button>
            </DialogActions>
          </Box>
        </Dialog>
        <Loader openLoader={false} />
      </Container>
    </Page>
  );
}
